// @ts-nocheck
/**
 * @fileoverview VAT/Tax Configuration and Display Logic - Theme Version
 * @description Provides tax configuration, calculation, translation and
 *              DOM rendering for VAT/GST display on product pages and
 *              cart drawer. Runs as a plain browser script via script tag.
 *              No export statements - all functions are scoped within
 *              the vatDisplayInitialized guard.
 *
 * Supported markets:
 *   AU - Australia        GST 10% (display enabled)
 *   GB - United Kingdom   VAT 20% (display enabled)
 *   SG - Singapore        GST  9% (display enabled)
 *   TW - Taiwan           VAT  5% (display enabled)
 *   ID - Indonesia        VAT 11% (display enabled)
 *   HK - Hong Kong        0% rate (display disabled)
 *   MY - Malaysia         0% rate (display disabled)
 *   CA - Canada                   (display disabled)
 *   US - United States            (display disabled)
 *
 * BEM Component: .vat-display
 *   Elements:  .vat-display__text
 *              .vat-display__icon
 *              .vat-display__tooltip
 *   Modifiers: .vat-display--cart
 *              .vat-display__tooltip--visible
 *
 * @version 1.0.0
 * @ticket AI-112789
 */

if (typeof window.vatDisplayInitialized === "undefined") {
  window.vatDisplayInitialized = true;

  // ---------------------------------------------------
  // Tax Configuration
  // ---------------------------------------------------

  /**
   * @typedef {Object} TaxConfigEntry
   * @property {boolean}      tax_included   - Whether tax is included in price
   * @property {number|null}  rate           - Tax rate as decimal e.g. 0.10
   * @property {string|null}  tax_name       - Short tax name e.g. 'GST'
   * @property {string|null}  [tax_fullname] - Full tax name for tooltip
   * @property {string|null}  tooltip        - Tooltip explanation text
   */

  /**
   * Tax configuration map keyed by ISO 3166-1 alpha-2 country code.
   * Countries with tax_included: false will not display any tax information.
   * @type {Object.<string, TaxConfigEntry>}
   * @constant
   */
  const TAX_CONFIG = {
    // --- TAX INCLUSIVE - WILL DISPLAY ---
    AU: {
      tax_included: true,
      rate: 0.1,
      tax_name: "GST",
      tax_fullname: "Goods and Services Tax",
      tooltip: "The GST (Goods and Services Tax) amount is a government-imposed tax included in the price.",
    },
    GB: {
      tax_included: true,
      rate: 0.2,
      tax_name: "VAT",
      tax_fullname: "Value Added Tax",
      tooltip: "The VAT (Value Added Tax) amount is a government-imposed tax included in the price.",
    },
    SG: {
      tax_included: true,
      rate: 0.09,
      tax_name: "GST",
      tax_fullname: "Goods and Services Tax",
      tooltip: "The GST (Goods and Services Tax) amount is a government-imposed tax included in the price.",
    },
    TW: {
      tax_included: true,
      rate: 0.05,
      tax_name: "VAT",
      tax_fullname: "Value Added Tax",
      tooltip: "The VAT (Value Added Tax) amount is a government-imposed tax included in the price.",
    },
    ID: {
      tax_included: true,
      rate: 0.11,
      tax_name: "VAT",
      tax_fullname: "Value Added Tax",
      tooltip: "The VAT (Value Added Tax) amount is a government-imposed tax included in the price.",
    },

    // --- TAX INCLUSIVE BUT ZERO RATE - NO DISPLAY ---
    HK: { tax_included: false, rate: null, tax_name: null, tooltip: null },
    MY: { tax_included: false, rate: null, tax_name: null, tooltip: null },

    // --- TAX EXCLUSIVE - NO DISPLAY ---
    CA: { tax_included: false, rate: null, tax_name: null, tooltip: null },
    US: { tax_included: false, rate: null, tax_name: null, tooltip: null },
  };

  // ---------------------------------------------------
  // Translations
  // ---------------------------------------------------

  /**
   * @typedef {Object} TranslationEntry
   * @property {string} includes_tax       - Product page tax-inclusive label
   * @property {string} cart_tax_total     - Cart total tax-inclusive label
   * @property {string} tax_label          - Short tax label with rate
   * @property {string} checkout_inclusive - Checkout page tax-inclusive label
   */

  /**
   * Translation strings keyed by locale code.
   * Supports exact locale match (e.g. 'en-AU') with fallback
   * to language-only match (e.g. 'en') then to 'en' default.
   *
   * Token replacements:
   *   {tax_name} - replaced with tax name e.g. GST, VAT
   *   {rate}     - replaced with rate percentage e.g. 10
   *   {amount}   - replaced with formatted currency e.g. A$6.55
   *
   * @type {Object.<string, TranslationEntry>}
   * @constant
   */
  const TRANSLATIONS = {
    en: {
      includes_tax: "{tax_name} of {amount} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-AU": {
      includes_tax: "{tax_name} of {amount} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-GB": {
      includes_tax: "Includes {tax_name} of {amount}",
      cart_tax_total: "Includes {tax_name} of {amount}",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-SG": {
      includes_tax: "{tax_name} of {amount} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-TW": {
      includes_tax: "{tax_name} of {amount} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-ID": {
      includes_tax: "{tax_name} of {amount} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    fr: {
      includes_tax: "{tax_name} de {amount} inclus",
      cart_tax_total: "{tax_name} de {amount} inclus",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Le prix du produit et la livraison sont {tax_name} inclus {amount}",
    },
    zh: {
      includes_tax: "已含 {tax_name} {amount}",
      cart_tax_total: "已含 {tax_name} {amount}",
      tax_label: "{tax_name}（{rate}%）",
      checkout_inclusive: "商品價格及運費已含 {tax_name} {amount}",
    },
    "zh-TW": {
      includes_tax: "已含 {tax_name} {amount}",
      cart_tax_total: "已含 {tax_name} {amount}",
      tax_label: "{tax_name}（{rate}%）",
      checkout_inclusive: "商品價格及運費已含 {tax_name} {amount}",
    },
    id: {
      includes_tax: "Sudah termasuk {tax_name} sebesar {amount}",
      cart_tax_total: "Sudah termasuk {tax_name} sebesar {amount}",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Harga produk dan pengiriman sudah termasuk {tax_name} {amount}",
    },
    "id-ID": {
      includes_tax: "Sudah termasuk {tax_name} sebesar {amount}",
      cart_tax_total: "Sudah termasuk {tax_name} sebesar {amount}",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Harga produk dan pengiriman sudah termasuk {tax_name} {amount}",
    },
  };

  // ---------------------------------------------------
  // Currency Symbols
  // ---------------------------------------------------

  /**
   * Currency symbol map keyed by ISO 4217 currency code.
   * Used to ensure consistent symbol display across locale
   * and currency combinations where Intl.NumberFormat
   * returns inconsistent results.
   * @type {Object.<string, string>}
   * @constant
   */
  const CURRENCY_SYMBOLS = {
    AUD: "A$",
    GBP: "£",
    SGD: "S$",
    TWD: "NT$",
    IDR: "Rp",
    CAD: "CA$",
    HKD: "HK$",
    MYR: "RM",
    USD: "$",
    EUR: "€",
  };

  // ---------------------------------------------------
  // Core Functions
  // ---------------------------------------------------

  /**
   * Retrieves the tax configuration for a given country code.
   * Performs case-insensitive lookup against TAX_CONFIG.
   *
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code e.g. 'AU'
   * @returns {TaxConfigEntry|null} Tax config entry or null if not found
   *
   * @example
   * getTaxConfig('AU');
   * // Returns { tax_included: true, rate: 0.10, tax_name: 'GST', ... }
   */
  function getTaxConfig(countryCode) {
    if (!countryCode) {
      return null;
    }
    return TAX_CONFIG[countryCode.toUpperCase()] || null;
  }

  /**
   * Extracts the tax amount from a tax-inclusive price.
   * Uses the formula: Tax = Price - (Price / (1 + rate))
   *
   * @param {number} priceInCents - Price in smallest currency unit
   * @param {number} rate         - Tax rate as decimal e.g. 0.10 for 10%
   * @returns {number} Tax amount in cents rounded to nearest integer
   *
   * @example
   * extractTaxFromPrice(6550, 0.10);
   * // Returns 595
   */
  function extractTaxFromPrice(priceInCents, rate) {
    if (!priceInCents || !rate) {
      return 0;
    }
    const priceExTax = priceInCents / (1 + rate);
    return Math.round(priceInCents - priceExTax);
  }

  /**
   * @typedef {Object} DisplayTaxResult
   * @property {boolean}  shouldDisplay - Whether tax should be displayed
   * @property {number}   [taxAmount]   - Tax amount in cents
   * @property {string}   [taxName]     - Short tax name e.g. 'GST'
   * @property {string}   [taxFullName] - Full tax name
   * @property {string}   [tooltip]     - Tooltip explanation text
   * @property {number}   [rate]        - Tax rate as decimal e.g. 0.10
   * @property {number}   [ratePercent] - Tax rate as percentage e.g. 10
   */

  /**
   * Determines whether tax should be displayed and calculates the amount.
   * Returns shouldDisplay: false for tax-exclusive countries or unknown
   * country codes.
   *
   * @param {number} priceInCents - Price in smallest currency unit
   * @param {string} countryCode  - ISO 3166-1 alpha-2 country code
   * @returns {DisplayTaxResult} Display tax result object
   *
   * @example
   * getDisplayTax(10000, 'AU');
   * // Returns { shouldDisplay: true, taxAmount: 909, taxName: 'GST', ... }
   *
   * getDisplayTax(10000, 'US');
   * // Returns { shouldDisplay: false }
   */
  function getDisplayTax(priceInCents, countryCode) {
    const config = getTaxConfig(countryCode);
    if (!config || !config.tax_included || !config.rate) {
      return { shouldDisplay: false };
    }
    return {
      shouldDisplay: true,
      taxAmount: extractTaxFromPrice(priceInCents, config.rate),
      taxName: config.tax_name,
      taxFullName: config.tax_fullname,
      tooltip: config.tooltip,
      rate: config.rate,
      ratePercent: Math.round(config.rate * 100),
    };
  }

  /**
   * Retrieves translation strings for a given locale.
   * Uses a fallback chain:
   *   1. Exact locale match    e.g. 'en-AU'
   *   2. Language-only match   e.g. 'en'
   *   3. Default English       'en'
   *
   * @param {string} locale - Locale code e.g. 'en-AU', 'zh', 'fr'
   * @returns {TranslationEntry} Translation strings for the locale
   *
   * @example
   * getTranslation('en-AU');
   * // Returns en-AU specific translations
   *
   * getTranslation('ja');
   * // Falls back to 'en' default translations
   */
  function getTranslation(locale) {
    if (!locale) {
      return TRANSLATIONS["en"];
    }
    if (TRANSLATIONS[locale]) {
      return TRANSLATIONS[locale];
    }
    const languageCode = locale.split("-")[0];
    if (TRANSLATIONS[languageCode]) {
      return TRANSLATIONS[languageCode];
    }
    return TRANSLATIONS["en"];
  }

  /**
   * Replaces named tokens in a translation template string.
   * Tokens use the format {tokenName}.
   * Unmatched tokens are left as-is.
   *
   * @param {string} template - Template string with {token} placeholders
   * @param {Object} values   - Key/value pairs to replace tokens with
   * @returns {string} Completed string with tokens replaced
   *
   * @example
   * applyTranslation('GST of {amount} included', { amount: 'A$6.55' });
   * // Returns 'GST of A$6.55 included'
   */
  function applyTranslation(template, values) {
    if (typeof template !== "string") {
      return "";
    }
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return values.hasOwnProperty(key) ? values[key] : match;
    });
  }

  /**
   * Formats a cents amount as a localised currency string.
   * Uses a hardcoded symbol map for consistent symbol display.
   *
   * @param {number} amountInCents - Amount in smallest currency unit
   * @param {string} currencyCode  - ISO 4217 currency code e.g. 'AUD'
   * @param {string} locale        - Locale for number formatting e.g. 'en-AU'
   * @returns {string} Formatted currency string e.g. 'A$6.55'
   *
   * @example
   * formatCurrency(655, 'AUD', 'en-AU');
   * // Returns 'A$6.55'
   */
  function formatCurrency(amountInCents, currencyCode, locale) {
    const amount = amountInCents / 100;
    if (!currencyCode) {
      return amount.toFixed(2);
    }
    const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
    try {
      const numberPart = new Intl.NumberFormat(locale || "en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      return `${symbol}${numberPart}`;
    } catch (e) {
      console.warn("VAT Display: Could not format number", e);
      return `${symbol}${amount.toFixed(2)}`;
    }
  }

  // ---------------------------------------------------
  // Tooltip
  // ---------------------------------------------------

  /**
   * Initialises tooltip show/hide behaviour for a VAT display block.
   * Handles hover (desktop), click/tap (mobile) and keyboard interactions.
   *
   * @param {HTMLElement} block - The VAT display block DOM element
   * @returns {void}
   */
  function initTooltip(block) {
    const icon = block.querySelector(".vat-display__icon");
    const tooltip = block.querySelector(".vat-display__tooltip");
    if (!icon || !tooltip) {
      return;
    }
    icon.addEventListener("mouseenter", () => tooltip.classList.add("vat-display__tooltip--visible"));
    icon.addEventListener("mouseleave", () => tooltip.classList.remove("vat-display__tooltip--visible"));
    icon.addEventListener("click", e => {
      e.stopPropagation();
      tooltip.classList.toggle("vat-display__tooltip--visible");
    });
    document.addEventListener("click", () => {
      tooltip.classList.remove("vat-display__tooltip--visible");
    });
    icon.addEventListener("focus", () => tooltip.classList.add("vat-display__tooltip--visible"));
    icon.addEventListener("blur", () => tooltip.classList.remove("vat-display__tooltip--visible"));
  }

  // ---------------------------------------------------
  // Block Renderer
  // ---------------------------------------------------

  /**
   * Renders VAT blocks and retries if cart drawer block is still empty.
   * Handles race conditions where drawer content loads asynchronously.
   *
   * @param {number} [retries=3] - Number of retry attempts remaining
   * @param {number} [delay=300] - Delay between retries in milliseconds
   * @returns {void}
   */
  function renderWithRetry(retries, delay) {
    retries = retries || 3;
    delay = delay || 300;

    window.renderVatDisplayBlocks();

    const drawerBlock = document.getElementById("vat-display-cart-cart-drawer");
    if (drawerBlock && drawerBlock.innerHTML.trim() === "" && retries > 0) {
      setTimeout(function () {
        renderWithRetry(retries - 1, delay);
      }, delay);
    }
  }

  /**
   * Renders the VAT display block with calculated tax amount and tooltip.
   * Updates the innerHTML of the block element with formatted tax info.
   * Hides the block via display:none for tax-exclusive countries.
   *
   * @param {HTMLElement} block - The VAT display block DOM element
   * @returns {void}
   */
  function renderBlock(block) {
    const countryCode = block.dataset.country;
    const priceInCents = parseInt(block.dataset.price, 10);
    const currencyCode = block.dataset.currency;
    const locale = block.dataset.locale;
    const context = block.dataset.context || "product";

    const taxInfo = getDisplayTax(priceInCents, countryCode);

    if (!taxInfo.shouldDisplay) {
      block.style.display = "none";
      return;
    }

    block.style.display = "";

    const formattedAmount = formatCurrency(taxInfo.taxAmount, currencyCode, locale);
    const translation = getTranslation(locale);
    const templateKey = context === "cart" ? "cart_tax_total" : "includes_tax";

    const displayText = applyTranslation(translation[templateKey], {
      tax_name: taxInfo.taxName,
      rate: taxInfo.ratePercent,
      amount: formattedAmount,
    });

    block.innerHTML = `
      <span class="vat-display__text">
        ${displayText}
        <span
          class="vat-display__icon"
          aria-label="${taxInfo.tooltip}"
          role="tooltip"
          tabindex="0"
        >ⓘ</span>
        <span class="vat-display__tooltip" role="tooltip">
          ${taxInfo.tooltip}
        </span>
      </span>
    `;

    initTooltip(block);
  }

  // ---------------------------------------------------
  // Cart Drawer Observer
  // ---------------------------------------------------

  let isRendering = false;
  let renderTimeout = null;

  /**
   * Attempts to render VAT display blocks.
   * Prevents concurrent renders via isRendering flag.
   * Requires window.renderVatDisplayBlocks to be defined.
   *
   * @returns {void}
   */
  function tryRenderVat() {
    if (typeof window.renderVatDisplayBlocks !== "function") {
      return;
    }

    // Allow re-render even during lock if any VAT block is empty
    // Handles DOM morphing after cart quantity updates
    const hasEmptyBlock = Array.from(document.querySelectorAll(".vat-display")).some(function (block) {
      return block.innerHTML.trim() === "";
    });

    if (isRendering && !hasEmptyBlock) {
      return;
    }

    isRendering = true;
    renderWithRetry(3, 300);
    setTimeout(function () {
      isRendering = false;
    }, 1500);
  }

  /**
   * Debounced render to prevent rapid-fire calls.
   * Cancels any pending render before scheduling a new one.
   *
   * @param {number} [delay=150] - Delay in milliseconds before rendering
   * @returns {void}
   */
  function debouncedRender(delay) {
    if (renderTimeout) {
      clearTimeout(renderTimeout);
    }
    renderTimeout = setTimeout(function () {
      tryRenderVat();
    }, delay || 150);
  }

  /**
   * Initialises all cart drawer watchers and event listeners.
   * Uses multiple methods to support different theme implementations:
   *   1. MutationObserver on cart summary element
   *   2. Cart panel attribute observer for visibility changes
   *   3. Fetch interceptor for cart API calls
   *   4. Click event listener for cart icon and quantity buttons
   *   5. Standard Shopify cart events
   *
   * @returns {void}
   */
  function watchForDrawer() {
    // Watch cart summary element for DOM changes
    const cartSummary = document.querySelector(".cart-drawer__summary, .cart-totals");
    if (cartSummary) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            debouncedRender(400);
          }
        });
      }).observe(cartSummary.parentElement || document.body, {
        childList: true,
        subtree: false,
      });
    }

    // Watch cart totals container for DOM morphing after quantity updates
    const cartTotalsContainer = document.querySelector(".cart-totals__container");
    if (cartTotalsContainer) {
      new MutationObserver(function () {
        const drawerBlock = document.getElementById("vat-display-cart-cart-drawer");
        if (drawerBlock && drawerBlock.innerHTML.trim() === "") {
          debouncedRender(200);
        }
      }).observe(cartTotalsContainer, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }

    // Watch cart panel visibility changes via attribute observer
    const cartPanel = document.querySelector('[data-cart-drawer], .cart-drawer, [aria-label="Cart"], [id*="cart"]');
    if (cartPanel) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "aria-hidden" ||
              mutation.attributeName === "open" ||
              mutation.attributeName === "class")
          ) {
            debouncedRender(500);
          }
        });
      }).observe(cartPanel, {
        attributes: true,
        childList: false,
        subtree: false,
      });
    }

    // Intercept fetch calls for cart operations
    const originalFetch = window.fetch;
    window.fetch = function () {
      const url = arguments[0];
      if (
        typeof url === "string" &&
        (url.includes("/cart/change") || url.includes("/cart/add") || url.includes("/cart/update"))
      ) {
        return originalFetch.apply(this, arguments).then(function (response) {
          debouncedRender(300); // ← reduced from 600
          return response;
        });
      }
      return originalFetch.apply(this, arguments);
    };

    // Click listeners for cart icon and quantity buttons
    document.addEventListener("click", function (event) {
      const cartTrigger = event.target.closest(
        '[href="/cart"], [data-cart-toggle], ' +
          '.cart-count-bubble, [aria-controls="cart-drawer"], ' +
          "[data-open-cart], .header__icon--cart, " +
          'a[href="/cart"]'
      );
      if (cartTrigger) {
        debouncedRender(600);
      }

      const quantityBtn = event.target.closest(
        '.quantity__button, [name="minus"], [name="plus"], ' +
          "cart-remove-button button, [data-quantity-minus], " +
          "[data-quantity-plus]"
      );
      if (quantityBtn) {
        debouncedRender(1000);
      }
    });

    // Watch for quantity input changes
    document.addEventListener("input", function (event) {
      const quantityInput = event.target.closest('.quantity__input, [data-quantity-input], input[name="updates[]"]');
      if (quantityInput) {
        debouncedRender(800);
      }
    });

    // Standard cart events
    document.addEventListener("cart:updated", function () {
      debouncedRender(400);
    });
    document.addEventListener("cart:refresh", function () {
      debouncedRender(400);
    });
    document.addEventListener("cart:open", function () {
      debouncedRender(400);
    });
    document.addEventListener("drawer:opened", function () {
      debouncedRender(400);
    });
  }

  /**
   * Finds all VAT display blocks on the page and renders them.
   * Exposed globally so cart drawer observer can call it
   * after AJAX cart updates.
   *
   * @returns {void}
   */
  window.renderVatDisplayBlocks = function () {
    const blocks = document.querySelectorAll(".vat-display");
    blocks.forEach(renderBlock);
  };

  // ---------------------------------------------------
  // Variant Change Handler
  // ---------------------------------------------------

  /**
   * Updates all VAT display blocks with a new variant price.
   * Called when the customer selects a different product variant.
   *
   * @param {number} newPriceInCents - New variant price in cents
   * @returns {void}
   */
  function updateVatForVariant(newPriceInCents) {
    const blocks = document.querySelectorAll(".vat-display");
    blocks.forEach(block => {
      block.dataset.price = newPriceInCents;
    });
    window.renderVatDisplayBlocks();
  }

  /**
   * Safely fetches variant data by ID and updates VAT display.
   * Validates HTTP response status before JSON parsing to prevent
   * SyntaxError on 404 responses or empty bodies.
   *
   * @param {string} variantId - Shopify variant ID
   * @returns {void}
   */
  function fetchVariantAndUpdate(variantId) {
    fetch(`/variants/${variantId}.js`)
      .then(function (response) {
        if (!response.ok) {
          throw new Error(`Variant ${variantId} not found (${response.status})`);
        }
        return response.json();
      })
      .then(function (variant) {
        if (variant && variant.price) {
          updateVatForVariant(variant.price);
        }
      })
      .catch(function (e) {
        console.warn("VAT Display: Variant fetch failed", e);
      });
  }

  /**
   * Initialises all variant change event listeners.
   *
   * Primary method uses the custom theme 'variant:update' event
   * which provides full variant data including price directly.
   * Fallback methods handle other theme implementations.
   *
   * Methods:
   *   1. variant:update  - Custom theme event (primary)
   *                        event.detail.resource.price
   *   2. variant:changed - Standard Shopify event
   *                        event.detail.variant.price
   *   3. variantChange   - Some custom theme event
   *                        event.detail.variant.price
   *
   * @returns {void}
   */
  function initVariantChangeListeners() {
    // Method 1: Custom theme event - primary handler
    // Provides full variant object via event.detail.resource
    document.addEventListener("variant:update", event => {
      const variant = event.detail?.resource;
      if (!variant) {
        return;
      }
      const price = parseInt(variant.price, 10);
      if (price) {
        updateVatForVariant(price);
      }
    });

    // Method 2: Standard Shopify variant change event
    document.addEventListener("variant:changed", event => {
      const variant = event.detail?.variant;
      if (variant && variant.price) {
        updateVatForVariant(variant.price);
      }
    });

    // Method 3: Custom event used by some themes
    document.addEventListener("variantChange", event => {
      const variant = event.detail?.variant;
      if (variant && variant.price) {
        updateVatForVariant(variant.price);
      }
    });
  }

  // ---------------------------------------------------
  // Initialise
  // ---------------------------------------------------

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      watchForDrawer();
      window.renderVatDisplayBlocks();
      initVariantChangeListeners();
    });
  } else {
    watchForDrawer();
    window.renderVatDisplayBlocks();
    initVariantChangeListeners();
  }
} // end vatDisplayInitialized guard
